import { useEffect, useState } from "react";
import { Anchor, Smartphone, X, Share, Plus, MoreVertical, Download } from "lucide-react";
import {
    usePwaInstall,
    isIOSSafari,
    installDismissedRecently,
    recordInstallDismiss,
} from "@/lib/pwa";

/**
 * InstallPrompt — the bottom banner nudging mobile-web captains to install
 * the PWA. Sean's feedback (Jul 2026): "downloading as a PWA isn't clear".
 *
 * Rules:
 *  - Mobile (iOS any browser / Android): always show unless installed,
 *    running standalone, inside the native app, or dismissed recently.
 *  - Desktop: only when Chrome's beforeinstallprompt is armed.
 *  - Dismiss → reappears after 2 new sessions (or 7 days), see lib/pwa.js.
 *  - iOS → step-by-step "Share → Add to Home Screen" modal.
 *  - Android without the native prompt → "⋮ menu → Add to Home screen" modal.
 */
export default function InstallPrompt() {
    const { available, isMobile, hasNativePrompt, platform, promptInstall } = usePwaInstall();
    const [dismissed, setDismissed] = useState(() => installDismissedRecently());
    const [helpVariant, setHelpVariant] = useState(null); // "ios" | "android" | null

    const show = available && !dismissed && (isMobile || hasNativePrompt);
    if (!show) return null;

    const dismiss = () => {
        recordInstallDismiss();
        setDismissed(true);
    };

    const tapInstall = async () => {
        const result = await promptInstall();
        if (result === "show-ios-help") setHelpVariant("ios");
        else if (result === "show-android-help") setHelpVariant("android");
        else if (result === "accepted") setDismissed(true);
    };

    return (
        <>
            <div
                data-testid="install-prompt"
                role="dialog"
                aria-label="Install Bosun's Mate"
                style={{
                    position: "fixed",
                    bottom: 16,
                    left: 16,
                    right: 16,
                    maxWidth: 460,
                    margin: "0 auto",
                    background:
                        "linear-gradient(180deg, rgba(20, 46, 74, 0.97), rgba(12, 33, 56, 0.97))",
                    border: "1px solid var(--bm-gold-dim)",
                    borderRadius: "var(--bm-r-lg)",
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    zIndex: 90,
                    boxShadow:
                        "0 24px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,181,114,0.10)",
                    backdropFilter: "blur(12px)",
                    animation: "bm-slide-up 380ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
            >
                <div
                    aria-hidden="true"
                    style={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: 10,
                        background:
                            "linear-gradient(135deg, var(--bm-bg-2), var(--bm-bg-1))",
                        border: "1px solid var(--bm-border)",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <Anchor size={20} color="var(--bm-gold)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        className="bm-display"
                        style={{ fontSize: 16, color: "var(--bm-gold)", lineHeight: 1.2 }}
                    >
                        Install the app — it's free
                    </div>
                    <div
                        style={{
                            fontSize: 12,
                            color: "var(--bm-text-muted)",
                            marginTop: 2,
                            lineHeight: 1.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {platform === "ios"
                            ? "Full-screen, on your home screen — no App Store needed."
                            : "One tap. Works offline on the dock."}
                    </div>
                </div>
                <button
                    data-testid="install-prompt-install-btn"
                    onClick={tapInstall}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "8px 14px", fontSize: 13, flexShrink: 0 }}
                >
                    <Download size={14} /> Install
                </button>
                <button
                    data-testid="install-prompt-dismiss-btn"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--bm-text-dim)",
                        cursor: "pointer",
                        padding: 4,
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <X size={16} />
                </button>
            </div>

            {helpVariant && (
                <InstallHelpModal variant={helpVariant} onClose={() => setHelpVariant(null)} />
            )}

            <style>{`
                @keyframes bm-slide-up {
                    from { opacity: 0; transform: translateY(24px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}

/**
 * InstallButton — reusable "Install app" trigger for settings/menus/landing
 * (requirement 5: discoverable outside the banner). Renders nothing when the
 * app can't be installed (already installed, standalone, or native app).
 */
export function InstallButton({ label = "Install the app", className = "bm-btn bm-btn-ghost", style, testid = "install-app-btn" }) {
    const { available, promptInstall } = usePwaInstall();
    const [helpVariant, setHelpVariant] = useState(null);

    if (!available) return null;

    const tap = async () => {
        const result = await promptInstall();
        if (result === "show-ios-help") setHelpVariant("ios");
        else if (result === "show-android-help") setHelpVariant("android");
    };

    return (
        <>
            <button data-testid={testid} onClick={tap} className={className} style={style}>
                <Download size={15} /> {label}
            </button>
            {helpVariant && (
                <InstallHelpModal variant={helpVariant} onClose={() => setHelpVariant(null)} />
            )}
        </>
    );
}

export function InstallHelpModal({ variant, onClose }) {
    const ios = variant === "ios";
    const nonSafariIOS = ios && !isIOSSafari();
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div
            data-testid={ios ? "ios-install-modal" : "android-install-modal"}
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.7)",
                display: "grid",
                placeItems: "center",
                padding: 20,
                zIndex: 200,
                animation: "bm-fade-in 200ms ease",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bm-card"
                style={{ padding: 28, maxWidth: 380, width: "100%" }}
            >
                <h3
                    className="bm-serif"
                    style={{ fontSize: 24, margin: "0 0 6px", fontWeight: 500 }}
                >
                    {ios ? "Install on iPhone" : "Install on Android"}
                </h3>
                <p
                    style={{
                        color: "var(--bm-text-muted)",
                        fontSize: 14,
                        margin: "0 0 22px",
                    }}
                >
                    Three taps and Bosun's Mate is on your home screen.
                </p>

                {ios ? (
                    <>
                        {nonSafariIOS && (
                            <div
                                data-testid="ios-safari-hint"
                                style={{
                                    fontSize: 13,
                                    color: "var(--bm-warn)",
                                    border: "1px solid var(--bm-border)",
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    marginBottom: 14,
                                }}
                            >
                                Tip: installing works best in <strong>Safari</strong>. Open
                                bosunsmate.app there first if these steps don't appear.
                            </div>
                        )}
                        <Step
                            n="1"
                            icon={<Share size={18} color="var(--bm-blue)" />}
                            title="Tap the Share button"
                            body="At the bottom of Safari (or top on iPad)."
                        />
                        <Step
                            n="2"
                            icon={<Plus size={18} color="var(--bm-blue)" />}
                            title="Choose “Add to Home Screen”"
                            body="Scroll the share sheet if you don't see it."
                        />
                        <Step
                            n="3"
                            icon={<Smartphone size={18} color="var(--bm-gold)" />}
                            title="Tap “Add”"
                            body="Top-right corner. Done — it's on your home screen."
                        />
                    </>
                ) : (
                    <>
                        <Step
                            n="1"
                            icon={<MoreVertical size={18} color="var(--bm-blue)" />}
                            title="Tap the ⋮ menu"
                            body="Top-right corner of Chrome."
                        />
                        <Step
                            n="2"
                            icon={<Download size={18} color="var(--bm-blue)" />}
                            title="Choose “Add to Home screen”"
                            body="Some phones say “Install app” instead."
                        />
                        <Step
                            n="3"
                            icon={<Smartphone size={18} color="var(--bm-gold)" />}
                            title="Confirm with “Install”"
                            body="Done — it's on your home screen like any app."
                        />
                    </>
                )}

                <button
                    data-testid="install-help-close-btn"
                    onClick={onClose}
                    className="bm-btn bm-btn-gold"
                    style={{ width: "100%", marginTop: 18 }}
                >
                    Got it
                </button>
            </div>
            <style>{`
                @keyframes bm-fade-in { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
}

function Step({ n, icon, title, body }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "10px 0",
                borderBottom: "1px solid var(--bm-border-soft)",
            }}
        >
            <div
                style={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 999,
                    background: "rgba(91, 185, 232, 0.08)",
                    border: "1px solid var(--bm-border)",
                    color: "var(--bm-blue)",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "grid",
                    placeItems: "center",
                }}
            >
                {n}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {icon}
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
                </div>
                <div
                    style={{
                        color: "var(--bm-text-muted)",
                        fontSize: 13,
                        marginTop: 2,
                    }}
                >
                    {body}
                </div>
            </div>
        </div>
    );
}
